import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Subtitle1,
  Subtitle2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { PeopleImportView } from '@lg/core';
import { DocumentType } from '@lg/shared';
import { useApi } from '../api/client';
import { DOC_LABEL, errorMessage, usd } from '../ui/format';

export interface PreviewRequest {
  submitterUpn: string;
  documentType: DocumentType;
  amountUsd: number;
  /** Changes on every pick so the same case can be re-run */
  nonce: number;
}

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  form: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.4fr 1fr auto',
    gap: tokens.spacingHorizontalM,
    alignItems: 'end',
  },
  chain: { display: 'flex', flexDirection: 'column', gap: 0, marginLeft: tokens.spacingHorizontalS },
  node: {
    position: 'relative',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  dot: {
    position: 'absolute',
    left: '-8px',
    top: '14px',
    width: '13px',
    height: '13px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
  },
  submitter: { borderLeftColor: tokens.colorNeutralStroke1 },
  approver: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexWrap: 'wrap' },
});

/** Pick a submitter, document type and amount → the approval chain (spec 15.3). */
export function RoutingPreview({
  view,
  request,
}: {
  view: PeopleImportView;
  request: PreviewRequest | null;
}) {
  const styles = useStyles();
  const api = useApi();
  const [submitterUpn, setSubmitter] = useState(
    view.submitters.find((s) => s.positionId.endsWith('-BUY'))?.upn ?? view.submitters[0]?.upn ?? '',
  );
  const [documentType, setDocType] = useState<DocumentType>('PurchaseOrder');
  const [amount, setAmount] = useState('40000');

  const run = useMutation({
    mutationFn: (input: { submitterUpn: string; documentType: DocumentType; amountUsd: number }) =>
      api.previewRouting({ importId: view.import.id, ...input }),
  });

  useEffect(() => {
    if (!request) return;
    setSubmitter(request.submitterUpn);
    setDocType(request.documentType);
    setAmount(String(request.amountUsd));
    run.mutate({
      submitterUpn: request.submitterUpn,
      documentType: request.documentType,
      amountUsd: request.amountUsd,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.nonce]);

  const amountUsd = Number(amount.replace(/[$,\s]/g, ''));
  const submitter = view.submitters.find((s) => s.upn === submitterUpn);
  const result = run.data;

  return (
    <Card className={styles.card}>
      <Subtitle1 as="h2">Routing preview</Subtitle1>
      <Body1>
        Who would approve this document in D365, based on reporting lines, approval limits and roles in this
        import.
      </Body1>
      <div className={styles.form}>
        <Field label="Submitter">
          <Dropdown
            value={submitter ? `${submitter.name} (${submitter.positionId})` : ''}
            selectedOptions={submitterUpn ? [submitterUpn] : []}
            onOptionSelect={(_, d) => setSubmitter(d.optionValue ?? '')}
          >
            {view.submitters.map((s) => (
              <Option key={s.upn} value={s.upn} text={`${s.name} (${s.positionId})`}>
                {s.name} ({s.positionId})
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Document">
          <Dropdown
            value={DOC_LABEL[documentType]}
            selectedOptions={[documentType]}
            onOptionSelect={(_, d) => setDocType((d.optionValue ?? 'PurchaseOrder') as DocumentType)}
          >
            {DocumentType.options.map((t) => (
              <Option key={t} value={t}>
                {DOC_LABEL[t] ?? t}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Amount (USD)">
          <Input value={amount} onChange={(_, d) => setAmount(d.value)} inputMode="numeric" />
        </Field>
        <Button
          appearance="primary"
          disabled={!submitterUpn || !Number.isFinite(amountUsd) || amountUsd <= 0 || run.isPending}
          onClick={() => run.mutate({ submitterUpn, documentType, amountUsd })}
        >
          Preview
        </Button>
      </div>
      {run.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{errorMessage(run.error)}</MessageBarBody>
        </MessageBar>
      )}
      {result && run.variables && (
        <>
          <div className={styles.chain}>
            <div className={`${styles.node} ${styles.submitter}`}>
              <Caption1>Submitted by</Caption1>
              <Body1>
                <b>
                  {view.submitters.find((s) => s.upn === run.variables?.submitterUpn)?.name ??
                    run.variables.submitterUpn}
                </b>{' '}
                · {DOC_LABEL[run.variables.documentType]} of {usd(run.variables.amountUsd)}
              </Body1>
            </div>
            {result.chain.map((step, i) => (
              <div key={i} className={styles.node}>
                <span className={styles.dot} />
                <Subtitle2>{step.label}</Subtitle2>
                {step.approvers.map((a, j) => (
                  <div key={j} className={styles.approver}>
                    <Body1>
                      <b>{a.name}</b>
                    </Body1>
                    {a.title && (
                      <Caption1>
                        {a.title} · {a.positionId}
                      </Caption1>
                    )}
                    {a.limitUsd !== undefined && (
                      <Badge appearance="tint" color="brand">
                        limit {usd(a.limitUsd)}
                      </Badge>
                    )}
                    {step.kind !== 'managerial' && (
                      <Badge appearance="outline">{step.kind === 'role' ? 'role' : 'escalation'}</Badge>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {result.gaps.map((g, i) => (
            <MessageBar key={`g${i}`} intent="error">
              <MessageBarBody>
                <b>Gap at {g.label}:</b> {g.message}
              </MessageBarBody>
            </MessageBar>
          ))}
          {result.warnings.map((w, i) => (
            <MessageBar key={`w${i}`} intent="warning">
              <MessageBarBody>{w}</MessageBarBody>
            </MessageBar>
          ))}
          {result.gaps.length === 0 && result.chain.length > 0 && (
            <Caption1>
              Mirrors the configured workflow rules; the sandbox smoke test confirms real D365 behaviour.
            </Caption1>
          )}
        </>
      )}
    </Card>
  );
}
